import { API } from '@/lib/api';
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Briefcase, Flame, Brain, FolderOpen, CreditCard, Receipt,
  Settings, MapPin, Shield, Lock, Headphones, ArrowLeftRight, LogOut, User, Camera, Loader2
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/layout/Navbar";
import { useState, useRef } from "react";



const ProMenuPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleProfilePicture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/upload/profile-picture`, formData, { withCredentials: true });
      await refreshUser();
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error("Failed to upload picture");
    } finally {
      setUploading(false);
    }
  };

  const handleSwitchMode = async () => {
    try {
      await axios.patch(`${API}/user/account-mode`, { mode: "personal" }, { withCredentials: true });
      await refreshUser();
      toast.success("Switched to Personal Mode");
      navigate("/");
    } catch (err) {
      toast.error("Failed to switch mode");
    }
  };

  const dashboard = [
    { to: "/professional/dashboard", icon: Briefcase, label: "Work Hub", desc: "View and manage incoming jobs", color: "text-emerald-600 bg-emerald-100" },
    { to: "/professional/activity", icon: Flame, label: "Activity", desc: "Ongoing and past job history", color: "text-orange-600 bg-orange-100" },
    { to: "/professional/earnings-insights", icon: Brain, label: "AI Insights", desc: "Earnings analytics and tips", color: "text-violet-600 bg-violet-100" },
    { to: "/professional/documents", icon: FolderOpen, label: "Documents", desc: "Upload and manage your documents", color: "text-blue-600 bg-blue-100" },
  ];

  const finance = [
    { to: "/wallet", icon: CreditCard, label: "Payment", desc: "Payouts and earnings", color: "text-green-600 bg-green-100" },
    { to: "/tax", icon: Receipt, label: "Tax Info", desc: "Tax documents and forms", color: "text-violet-600 bg-violet-100" },
    { to: "/plus-card", icon: CreditCard, label: "Plus Card", desc: "Your Stack Plus Card", color: "text-amber-600 bg-amber-100" },
  ];

  const settings = [
    { to: "/account", icon: Settings, label: "Manage Account", desc: "Account details and preferences", color: "text-stone-600 bg-stone-100" },
    { to: "/address", icon: MapPin, label: "Edit Address", desc: "Update your address", color: "text-rose-600 bg-rose-100" },
    { to: "/insurance", icon: Shield, label: "Insurance", desc: "Coverage and claims", color: "text-sky-600 bg-sky-100" },
    { to: "/settings", icon: Settings, label: "App Settings", desc: "Notifications and preferences", color: "text-stone-600 bg-stone-100" },
    { to: "/professional/support", icon: Headphones, label: "Support Chat", desc: "Get help from Stack support", color: "text-teal-600 bg-teal-100" },
  ];

  const Section = ({ title, items }) => (
    <div className="mb-6">
      <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">{title}</p>
      <div className="space-y-2">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center gap-3 hover:border-emerald-200 transition-colors block"
            data-testid={`pro-menu-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${item.color}`}>
              <item.icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-emerald-950 text-sm">{item.label}</p>
              <p className="text-xs text-stone-500">{item.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="pro-menu-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>

        {/* Pro Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 rounded-2xl p-5 mb-6 flex items-center gap-4">
            <div className="relative">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-emerald-400 object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center border-2 border-emerald-400">
                  <User className="w-7 h-7 text-white" />
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute -bottom-1 -right-1 w-7 h-7 bg-lime-400 rounded-full flex items-center justify-center border-2 border-emerald-800 hover:bg-lime-300 transition-colors"
                data-testid="change-picture-btn"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 text-emerald-950 animate-spin" /> : <Camera className="w-3.5 h-3.5 text-emerald-950" />}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePicture} className="hidden" data-testid="picture-input" />
            </div>
            <div>
              <p className="font-semibold text-white">{user?.name}</p>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/30 text-emerald-200 px-2 py-0.5 rounded">Pro Mode</span>
            </div>
          </div>

          <Section title="Dashboard" items={dashboard} />
          <Section title="Finance" items={finance} />
          <Section title="Settings" items={settings} />

          {/* Actions */}
          <div className="space-y-2 mt-6">
            <button
              onClick={handleSwitchMode}
              className="w-full bg-white rounded-2xl border border-emerald-100 p-4 flex items-center gap-3 text-emerald-700 hover:bg-emerald-50 transition-colors"
              data-testid="switch-to-personal-btn"
            >
              <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
                <ArrowLeftRight className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="font-medium text-sm">Switch to Personal Mode</p>
            </button>
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="w-full bg-white rounded-2xl border border-red-100 p-4 flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors"
              data-testid="sign-out-btn"
            >
              <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
                <LogOut className="w-4 h-4 text-red-500" />
              </div>
              <p className="font-medium text-sm">Sign Out</p>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProMenuPage;
